// The API client for native apps (and, later, the web): the same method table as apps/web/src/lib/api.ts, with the
// origin, the session token and fetch passed in. Native clients send the session as a bearer header (Better Auth's
// bearer plugin); a response that signs someone in carries the new token in `set-auth-token`.
import { API_PREFIX } from "./index.js";
export class RequestError extends Error {
    status;
    code;
    constructor(status, code, message) {
        super(message ?? code);
        this.status = status;
        this.code = code;
    }
}
export function createApiClient(o) {
    const f = o.fetch ?? ((...a) => fetch(...a));
    const base = o.origin.replace(/\/$/, "") + API_PREFIX;
    async function send(method, path, body, contentType) {
        const token = await o.token();
        const res = await f(`${base}${path}`, {
            method, body, ...(o.credentials ? { credentials: o.credentials } : {}),
            headers: { ...o.headers, ...(contentType ? { "content-type": contentType } : {}), ...(token ? { authorization: `Bearer ${token}` } : {}) },
        });
        o.onVersion?.(res.headers.get("x-app-version"));
        const next = res.headers.get("set-auth-token");
        if (next)
            o.onToken?.(next);
        if (!res.ok) {
            let err = { error: `http_${res.status}` };
            // Our routes answer {error, message}; Better Auth's (email-code sign-in) answer {code, message}.
            try {
                const b = (await res.json());
                err = { error: b.error ?? b.code ?? err.error, message: b.message };
            }
            catch { /* non-json */ }
            throw new RequestError(res.status, err.error, err.message);
        }
        return res;
    }
    async function request(method, path, json) {
        const res = await send(method, path, json !== undefined ? JSON.stringify(json) : undefined, json !== undefined ? "application/json" : undefined);
        return (await res.json());
    }
    /** Raw-body upload (the avatar): same auth as request(), no JSON wrapping. */
    async function upload(path, blob) {
        return (await (await send("POST", path, blob, blob.type)).json());
    }
    return {
        health: () => request("GET", "/healthz"),
        avatarChoices: () => request("GET", "/me/avatar/choices"),
        useProviderPicture: (provider) => request("PUT", "/me/avatar", { provider }),
        uploadAvatar: (blob) => upload("/me/avatar", blob),
        removeAvatar: () => request("DELETE", "/me/avatar"),
        // Unsealing moments, notes left forward, predictions (PLAN.md §Unsealing moments)
        moment: (clubId, bookId) => request("POST", `/clubs/${clubId}/books/${bookId}/moment`).then((r) => r.moment),
        dismissMoment: (clubId, bookId) => request("DELETE", `/clubs/${clubId}/books/${bookId}/moment`),
        forwardNotes: (clubId, bookId) => request("GET", `/clubs/${clubId}/books/${bookId}/forward-notes`),
        leaveForwardNote: (clubId, bookId, input) => request("POST", `/clubs/${clubId}/books/${bookId}/forward-notes`, input),
        editForwardNote: (clubId, bookId, noteId, input) => request("PATCH", `/clubs/${clubId}/books/${bookId}/forward-notes/${noteId}`, input),
        deleteForwardNote: (clubId, bookId, noteId) => request("DELETE", `/clubs/${clubId}/books/${bookId}/forward-notes/${noteId}`),
        shareForwardNote: (clubId, bookId, noteId) => request("POST", `/clubs/${clubId}/books/${bookId}/forward-notes/${noteId}/share`),
        predictions: (clubId, bookId) => request("GET", `/clubs/${clubId}/books/${bookId}/predictions`),
        savePrediction: (clubId, bookId, input) => request("PUT", `/clubs/${clubId}/books/${bookId}/prediction`, input),
        deletePrediction: (clubId, bookId) => request("DELETE", `/clubs/${clubId}/books/${bookId}/prediction`),
        me: () => request("GET", "/me"),
        updateMe: (displayName) => request("PATCH", "/me", { displayName }),
        updateProfile: (patch) => request("PATCH", "/me", patch),
        useInitials: () => request("PUT", "/me/avatar", { initials: true }),
        profile: (id) => request("GET", `/profiles/${encodeURIComponent(id)}`),
        sharedProfile: (slug) => request("GET", `/p/${encodeURIComponent(slug)}`),
        myQuotes: () => request("GET", "/me/quotes"),
        cardSeen: (card) => request("POST", "/me/card-seen", { card }),
        welcomed: () => request("POST", "/me/welcome"),
        deleteMe: () => request("DELETE", "/me"),
        createClub: (input) => request("POST", "/clubs", input),
        clubLink: (slug) => request("GET", `/me/club-link?slug=${encodeURIComponent(slug)}`),
        club: (id) => request("GET", `/clubs/${id}`),
        updateClub: (id, patch) => request("PATCH", `/clubs/${id}`, patch),
        deleteClub: (id) => request("DELETE", `/clubs/${id}`),
        transferClub: (id, userId) => request("POST", `/clubs/${id}/transfer`, { userId }),
        members: (id) => request("GET", `/clubs/${id}/members`),
        directory: (p) => {
            const qs = new URLSearchParams();
            for (const [k, v] of Object.entries(p))
                if (v !== undefined && v !== null && v !== "" && k !== "genres")
                    qs.set(k, String(v));
            for (const g of p.genres ?? [])
                qs.append("genre", g);
            return request("GET", `/directory?${qs}`);
        },
        publicClub: (id) => request("GET", `/clubs/${id}/public`),
        joinClub: (id) => request("POST", `/clubs/${id}/join`),
        requestJoin: (id, note) => request("POST", `/clubs/${id}/requests`, { note }),
        cancelRequest: (id) => request("DELETE", `/clubs/${id}/requests/mine`),
        blocks: (id) => request("GET", `/clubs/${id}/blocks`),
        unblock: (id, userId) => request("DELETE", `/clubs/${id}/blocks/${userId}`),
        joinRequests: (id) => request("GET", `/clubs/${id}/requests`),
        decideRequest: (id, rid, approve) => request("POST", `/clubs/${id}/requests/${rid}/${approve ? "approve" : "decline"}`),
        setVisibility: (id, visibility) => request("PUT", `/clubs/${id}/visibility`, { visibility }),
        clubStats: (id) => request("GET", `/clubs/${id}/stats`),
        clubActivity: (id, before, limit = 8) => request("GET", `/clubs/${id}/activity?limit=${limit}${before ? `&before=${encodeURIComponent(before)}` : ""}`),
        setRole: (id, userId, role) => request("PATCH", `/clubs/${id}/members/${userId}`, { role }),
        removeMember: (id, userId, block = false) => request("DELETE", `/clubs/${id}/members/${userId}${block ? "?block=1" : ""}`),
        invites: (id) => request("GET", `/clubs/${id}/invites`),
        createInvite: (id, input) => request("POST", `/clubs/${id}/invites`, input),
        revokeInvite: (id, inviteId) => request("DELETE", `/clubs/${id}/invites/${inviteId}`),
        searchBooks: (q) => request("GET", `/catalog/search?q=${encodeURIComponent(q)}`),
        clubBooks: (id) => request("GET", `/clubs/${id}/books`),
        placeBook: (id, input) => request("POST", `/clubs/${id}/books/place`, input),
        addBook: (id, hardcoverId) => request("POST", `/clubs/${id}/books`, { hardcoverId }),
        suggestions: (id) => request("GET", `/clubs/${id}/books/suggestions`),
        /** "Want to read" on a suggestion, on or off */
        setInterest: (id, bookId, wanted) => request("PUT", `/clubs/${id}/books/${bookId}/interest`, { wanted }),
        revive: (id, bookId) => request("POST", `/clubs/${id}/books/${bookId}/revive`),
        moveToPast: (id, bookId) => request("POST", `/clubs/${id}/books/${bookId}/past`),
        setNote: (id, bookId, note) => request("PUT", `/clubs/${id}/books/${bookId}/note`, { note }),
        setPinned: (id, bookId, pinned) => request("PUT", `/clubs/${id}/books/${bookId}/pin`, { pinned }),
        dismissNudge: (id) => request("POST", `/clubs/${id}/nudge`),
        setBookStatus: (id, bookId, status) => request("PATCH", `/clubs/${id}/books/${bookId}`, { status }),
        /** start a book; with `finishCurrent` the current book is finished first, dated today */
        startBook: (id, bookId, finishCurrent) => request("PATCH", `/clubs/${id}/books/${bookId}`, { status: "current", finishCurrent }),
        updateBook: (id, bookId, input) => request("PATCH", `/clubs/${id}/books/${bookId}`, input),
        removeBook: (id, bookId) => request("DELETE", `/clubs/${id}/books/${bookId}`),
        finishLine: (id, bookId, input) => request("POST", `/clubs/${id}/books/${bookId}/finish-line`, input),
        giveUp: (id, bookId, input) => request("POST", `/clubs/${id}/books/${bookId}/give-up`, input),
        keepReading: (id, bookId) => request("POST", `/clubs/${id}/books/${bookId}/keep-reading`, {}),
        /** "You" on a finished book: read it, reading it now, or didn't read it */
        setMine: (id, bookId, state) => request("PUT", `/clubs/${id}/books/${bookId}/mine`, { state }),
        /** "I've read this" on an on-deck book */
        setReadBefore: (id, bookId, state) => request("PUT", `/clubs/${id}/books/${bookId}/read-before`, { state }),
        /** on the current book: reading it after all (true) or sitting it out (false) */
        setRereading: (id, bookId, rereading) => request("PUT", `/clubs/${id}/books/${bookId}/rereading`, { rereading }),
        meeting: (id, bookId) => request("GET", `/clubs/${id}/books/${bookId}/meeting`),
        tickAgenda: (id, bookId, key, done) => request("POST", `/clubs/${id}/books/${bookId}/agenda`, { key, done }),
        flagComment: (id, commentId, flagged) => request("POST", `/clubs/${id}/comments/${commentId}/flag`, { flagged }),
        savePlan: (id, bookId, input) => request("PUT", `/clubs/${id}/books/${bookId}/plan`, input),
        /** with `audioSeconds`, also records how long the audiobook is (when Hardcover didn't know) */
        setFormat: (id, bookId, format, audioSeconds) => request("PUT", `/clubs/${id}/books/${bookId}/format`, { format, ...(audioSeconds ? { audioSeconds } : {}) }),
        revealAnswer: (id, commentId) => request("POST", `/clubs/${id}/comments/${commentId}/reveal`),
        answerComment: (id, commentId, answered) => request("POST", `/clubs/${id}/comments/${commentId}/answered`, { answered }),
        discussComment: (id, commentId, discussed) => request("POST", `/clubs/${id}/comments/${commentId}/discussed`, { discussed }),
        notes: (id, bookId) => request("GET", `/clubs/${id}/books/${bookId}/notes`),
        addNote: (id, bookId, input) => request("POST", `/clubs/${id}/books/${bookId}/notes`, input),
        editNote: (id, bookId, noteId, input) => request("PATCH", `/clubs/${id}/books/${bookId}/notes/${noteId}`, input),
        deleteNote: (id, bookId, noteId) => request("DELETE", `/clubs/${id}/books/${bookId}/notes/${noteId}`),
        shareNote: (id, bookId, noteId, flag) => request("POST", `/clubs/${id}/books/${bookId}/notes/${noteId}/share`, { flag }),
        unshareNote: (id, bookId, noteId) => request("DELETE", `/clubs/${id}/books/${bookId}/notes/${noteId}/share`),
        recap: (id, bookId) => request("GET", `/clubs/${id}/books/${bookId}/recap`),
        rateBook: (id, bookId, stars) => request("PUT", `/clubs/${id}/books/${bookId}/rating`, { stars }),
        clearRating: (id, bookId) => request("DELETE", `/clubs/${id}/books/${bookId}/rating`),
        setMeeting: (id, meeting) => request("PUT", `/clubs/${id}/meeting`, meeting),
        meetingDetail: (id) => request("GET", `/clubs/${id}/meeting`),
        setRsvp: (id, status) => request("PUT", `/clubs/${id}/meeting/rsvp`, { status }),
        setMeetingNotes: (id, notes) => request("PUT", `/clubs/${id}/meeting/notes`, { notes }),
        questions: (id) => request("GET", `/clubs/${id}/questions`),
        openVote: (id, closesAt) => request("POST", `/clubs/${id}/vote`, { closesAt }),
        setBallot: (id, ranks) => request("PUT", `/clubs/${id}/vote/ballot`, { ranks }),
        closeVote: (id) => request("POST", `/clubs/${id}/vote/close`),
        pickWinner: (id, clubBookId) => request("POST", `/clubs/${id}/vote/winner`, { clubBookId }),
        dismissVote: (id) => request("DELETE", `/clubs/${id}/vote`),
        skipTurn: (id) => request("POST", `/clubs/${id}/turn/skip`),
        addQuestion: (id, input) => request("POST", `/clubs/${id}/questions`, input),
        editQuestion: (id, qid, input) => request("PATCH", `/clubs/${id}/questions/${qid}`, input),
        deleteQuestion: (id, qid) => request("DELETE", `/clubs/${id}/questions/${qid}`),
        discussQuestion: (id, qid, discussed) => request("POST", `/clubs/${id}/questions/${qid}/discussed`, { discussed }),
        progress: (id, bookId) => request("GET", `/clubs/${id}/books/${bookId}/progress`),
        updateProgress: (id, bookId, input) => request("POST", `/clubs/${id}/books/${bookId}/progress`, input),
        board: (id, cursor) => request("GET", `/clubs/${id}/board${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`),
        boardThread: (id, postId) => request("GET", `/clubs/${id}/board/${postId}`),
        boardPost: (id, body) => request("POST", `/clubs/${id}/board`, { body }),
        boardReply: (id, postId, body) => request("POST", `/clubs/${id}/board/${postId}/replies`, { body }),
        boardEdit: (id, postId, body) => request("PATCH", `/clubs/${id}/board/${postId}`, { body }),
        boardDelete: (id, postId) => request("DELETE", `/clubs/${id}/board/${postId}`),
        boardPin: (id, postId, until) => request("POST", `/clubs/${id}/board/pin`, { postId, until }),
        boardUnpin: (id) => request("DELETE", `/clubs/${id}/board/pin`),
        boardSeen: (id) => request("POST", `/clubs/${id}/board/seen`),
        boardMovePreview: (id, postId) => request("GET", `/clubs/${id}/board/${postId}/move`),
        boardMove: (id, postId, position) => request("POST", `/clubs/${id}/board/${postId}/move`, position != null ? { position } : {}),
        discussion: (id, bookId) => request("GET", `/clubs/${id}/books/${bookId}/comments`),
        addComment: (id, bookId, input) => request("POST", `/clubs/${id}/books/${bookId}/comments`, input),
        editComment: (id, commentId, input) => request("PATCH", `/clubs/${id}/comments/${commentId}`, input),
        markSeen: (id, bookId) => request("POST", `/clubs/${id}/books/${bookId}/comments/seen`),
        deleteComment: (id, commentId) => request("DELETE", `/clubs/${id}/comments/${commentId}`),
        hardcoverSync: (force = false) => request("POST", `/hardcover/sync${force ? "?force=1" : ""}`),
        hardcoverUnlink: () => request("DELETE", "/hardcover/link"),
        // Notifications (PLAN.md §Notifications)
        notifications: (cursor) => request("GET", `/notifications${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`),
        notificationCount: () => request("GET", "/notifications/count"),
        markRead: (input) => request("POST", "/notifications/read", input),
        notificationPrefs: () => request("GET", "/notification-prefs"),
        updateNotificationPrefs: (patch) => request("PUT", "/notification-prefs", patch),
        rememberZone: (timeZone) => request("POST", "/notification-prefs/time-zone", { timeZone }),
        pushKey: () => request("GET", "/push/public-key"),
        addPushSubscription: (sub) => request("POST", "/push-subscriptions", { platform: "web", ...sub }),
        removePushSubscription: (endpoint) => request("DELETE", "/push-subscriptions", { endpoint }),
        removeDevice: (id) => request("DELETE", `/push-subscriptions/${encodeURIComponent(id)}`),
        // Email addresses (PLAN.md §Email addresses and email sign-in)
        myEmails: () => request("GET", "/me/emails"),
        addEmail: (address, forNotifications) => request("POST", "/me/emails", { address, forNotifications }),
        confirmEmail: (address, code) => request("POST", "/me/emails/confirm", { address, code }),
        setNotifyEmail: (id) => request("PUT", "/me/emails/notify", { id }),
        removeEmail: (id) => request("DELETE", `/me/emails/${encodeURIComponent(id)}`),
        confirmEmailLink: (id, code) => request("POST", "/emails/confirm-link", { id, code }),
        // Signing in with a code (a Better Auth plugin, under /api/auth)
        emailCodeSend: (email, next) => request("POST", "/auth/email-code/send", { email, next }),
        emailCodeVerify: (email, code) => request("POST", "/auth/email-code/verify", { email, code }),
        clubMute: (id) => request("GET", `/clubs/${id}/mute`),
        setClubMute: (id, muted) => request("PUT", `/clubs/${id}/mute`, { muted }),
        peekInvite: (code) => request("GET", `/invites/${code}`),
        acceptInvite: (code) => request("POST", `/invites/${code}/accept`),
        // Sessions for native clients (Better Auth, under /api/auth). Social sign-in runs in the system browser:
        // socialStart gives the provider's URL for a callbackURL in the app's own scheme (PLAN.md §Native apps).
        session: () => request("GET", "/auth/get-session"),
        signOut: () => request("POST", "/auth/sign-out", {}),
        socialStart: (provider, callbackURL) => request("POST", "/auth/sign-in/social", { provider, callbackURL, disableRedirect: true }),
        /** A provider's ID token from a native sheet (Sign in with Apple): the answer's set-auth-token is the session. */
        signInWithIdToken: (provider, token, nonce) => request("POST", "/auth/sign-in/social", { provider, idToken: { token, nonce } }),
    };
}
//# sourceMappingURL=client.js.map